import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { useConsents } from "../../hooks/useConsents";
import { LegalLinks } from "../legal/LegalLinks";
import { useAuth } from "../../context/AuthContext";
import { isCompanyAdmin } from "../../utils/role";

const KINDS = ["terms", "privacy", "eula", "biometric"];
const LABEL_KEY = { terms: "termsLabel", privacy: "privacyLabel", eula: "eulaLabel", biometric: "bioLabel" };

// سجل الموافقات الخاص بالمستخدم + سحب الموافقة الحيوية في أي وقت (GDPR Art.7(3)).
export const ConsentsCard = () => {
  const { t, i18n } = useTranslation();
  const { state, accept, withdraw } = useConsents();
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(i18n.language === "ar" ? "ar" : "en") : "—");

  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      toast.success(t("consent.saved"));
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy("");
    }
  };

  return (
    <NeuCard data-testid="consents-card">
      <div className="flex items-start justify-between gap-3">
        <p className="label-xs">{t("consent.cardTitle")}</p>
        <FileCheck2 className="h-5 w-5 text-primary" />
      </div>
      <div className="mt-4 grid gap-2">
        {KINDS.map((kind) => (
          <div key={kind} className="neu-in flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-xs"
               data-testid={`consent-status-${kind}`}>
            <span className="font-bold">{t(`consent.${LABEL_KEY[kind]}`)}</span>
            <span className="text-muted-foreground">
              {state?.accepted?.[kind]
                ? `v${state.accepted[kind].version} · ${fmt(state.accepted[kind].at)}`
                : t("consent.notAccepted")}
            </span>
          </div>
        ))}
      </div>
      {isCompanyAdmin(user) ? (
        <>
          {state?.biometric ? (
            <NeuButton variant="danger" className="mt-4 w-full justify-center py-3 sm:w-auto sm:px-6" data-testid="consent-withdraw-bio"
              disabled={busy === "bio"} onClick={() => act("bio", () => withdraw(null))}>
              {t("consent.withdrawBio")}
            </NeuButton>
          ) : (
            <NeuButton variant="primary" className="mt-4 w-full justify-center py-3 sm:w-auto sm:px-6" data-testid="consent-grant-bio"
              disabled={busy === "bio"} onClick={() => act("bio", () => accept(["biometric"]))}>
              {t("consent.grantBio")}
            </NeuButton>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">{t("consent.withdrawHint")}</p>
        </>
      ) : null}
      <div className="mt-4">
        <LegalLinks />
      </div>
    </NeuCard>
  );
};
